import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, AlertCircle } from "lucide-react";
import { useAuth } from "../AuthContext";
import { formatErr } from "../api";
import Logo from "../../components/Logo";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      const from = location.state?.from;
      navigate(typeof from === "string" && from.startsWith("/admin/") ? from : "/admin");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1C35] grain flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="lg" testId="admin-login-logo" />
          <div className="mt-1 text-xs uppercase tracking-widest text-slate-400 font-semibold">Admin Panel</div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl p-7 card-shadow" data-testid="admin-login-form">
          <h1 className="font-display font-bold text-xl text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-1">Access the Repairro admin console.</p>

          {error && (
            <div className="mt-4 flex items-start gap-2 bg-red-50 text-red-700 border border-red-100 rounded-xl p-3 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}

          <div className="mt-5">
            <label className="text-sm font-medium text-slate-700">Email</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="admin@servtek.in"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium text-slate-700">Password</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full bg-[#1A3C6E] hover:bg-[#132D53] text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <p className="mt-5 text-xs text-slate-500 text-center">
            {process.env.NODE_ENV !== "production" && (
              <>Default: <span className="font-mono">admin@servtek.in</span> / <span className="font-mono">Servtek@123</span></>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
